
import React, { useState, useEffect, useRef } from "react";


const TestVoiceRecorder = () => {

  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [partial, setPartial] = useState("");
  const [assistantText, setAssistantText] = useState("");
  const [error, setError] = useState("");
  const [speaking, setSpeaking] = useState(false);

  const recognitionRef = useRef(null);

  // Setup SpeechRecognition (Chrome/Edge support; Safari partial)
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setError("SpeechRecognition not supported in this browser.");
      return;
    }

    const recognition = new SR();
    recognition.continuous = false;         // one utterance at a time
    recognition.interimResults = true;      // show live partials
    recognition.lang = "en-US";

    recognition.onstart = () => {
      setError("");
      setTranscript("");
      setPartial("");
      setAssistantText("");
      setListening(true);
    };

    recognition.onerror = (e) => {
      setError(e.error || "Mic error");
      setListening(false);
    };

    recognition.onresult = (event) => {
      let finalText = "";
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const chunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += chunk;
        else interimText += chunk;
      }
      if (interimText) setPartial(interimText);
      if (finalText) setTranscript((prev) => (prev ? prev + " " + finalText : finalText));
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognitionRef.current = recognition;
    return () => {
      recognition.stop?.();
      recognitionRef.current = null;
    };
  }, []);

  const startListening = () => {
    if (!recognitionRef.current) return;
    setAssistantText("");
    setError("");
    try {
      recognitionRef.current.start();
    } catch (e) {
      // start() may throw if already started
    }
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
  };

  // // Send the final transcript to your backend (which calls OpenAI)
  // const sendToAssistant = async () => {
  //   if (!transcript.trim()) return;
  //   setError("");
  //   try {
  //     // Adapt the payload to match your existing askAI endpoint
  //     const res = await axios.post(apiUrl, {
  //       // Example: if your backend expects messages
  //       messages: [{ role: "user", content: transcript }],
  //     });
  //     // Expect your server to return text content or your block format.
  //     // If you return blocks, convert them to a single speakable string here.
  //     const text = extractSpeakableText(res.data);
  //     setAssistantText(text);
  //     speak(text);
  //   } catch (e) {
  //     setError(e?.response?.data?.error || e.message);
  //   }
  // };

  // Simple helper to speak text
  const speak = (text) => {
    const synth = window.speechSynthesis;
    if (!synth) return; // TTS not supported
    const utter = new SpeechSynthesisUtterance(text);
    utter.onstart = () => setSpeaking(true);
    utter.onend = () => setSpeaking(false);
    // Optional voice selection:
    // const voice = synth.getVoices().find(v => v.lang.startsWith("en"));
    // if (voice) utter.voice = voice;
    synth.cancel(); // stop anything currently speaking
    synth.speak(utter);
  };

  // Turn your API response into a speakable string.
  // If your backend returns { functionName, args } (e.g., render_blocks),
  // flatten it to text here.
  function extractSpeakableText(data) {
    // Case A: direct text
    if (typeof data === "string") return data;

    // Case B: { functionName, args: { blocks: [...] } }
    if (data?.args?.blocks) {
      const parts = [];
      for (const b of data.args.blocks) {
        if (b.type === "title" && b.text) parts.push(b.text);
        if (b.type === "text" && b.markdown) parts.push(b.markdown);
        if (b.type === "steps" && Array.isArray(b.items)) parts.push(b.items.join(". "));
        if (b.type === "list" && Array.isArray(b.items)) parts.push(b.items.join(". "));
        if (b.type === "callout" && b.markdown) parts.push(b.markdown);
        // you can optionally read code aloud, but most people skip it
      }
      return parts.join("\n");
    }

    // Case C: { content: "..."} or similar
    if (data?.content) return String(data.content);

    return "I couldn't parse the response.";
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button
          onClick={listening ? stopListening : startListening}
          className={`px-4 py-2 rounded ${listening ? "bg-red-600 text-white" : "bg-blue-600 text-white"}`}
          aria-pressed={listening}
        >
          {listening ? "Stop" : "Start"} Mic
        </button>

        <button
          onClick={""}
          // onClick={sendToAssistant}
          className="px-4 py-2 rounded bg-green-600 text-white"
          disabled={!transcript}
        >
          Send to ChatGPT
        </button>

        <button
          onClick={() => speak(assistantText)}
          className="px-4 py-2 rounded bg-gray-700 text-white"
          disabled={!assistantText || speaking}
        >
          {speaking ? "Speaking..." : "Replay Answer"}
        </button>
      </div>

      {error && <p className="text-red-600 text-sm">Error: {error}</p>}

      <div>
        <label className="block text-sm font-medium">Partial (live):</label>
        <div className="p-2 border rounded bg-gray-50 min-h-[2.5rem]">{partial}</div>
      </div>

      <div>
        <label className="block text-sm font-medium">Final Transcript:</label>
        <div className="p-2 border rounded min-h-[3rem]">{transcript}</div>
      </div>

      <div>
        <label className="block text-sm font-medium">Assistant Reply:</label>
        <div className="p-2 border rounded min-h-[3rem] whitespace-pre-wrap">{assistantText}</div>
      </div>
    </div>
  );
};

export default TestVoiceRecorder;
